import * as React from 'react'
import { getTemplates } from '@/actions/templates/get-templates'
import { useAccountMatterStore } from '../../store/use-account-matter-store'
import { usePromptTemplatesStore } from '@/lib/store/use-prompt-templates-store'

export function usePollForPromptTemplateChanges() {
  const accountId = useAccountMatterStore((store) => store.accountId)
  const promptTemplates = usePromptTemplatesStore((store) => store.promptTemplates);
  const setPromptTemplates = usePromptTemplatesStore((store) => store.setPromptTemplates);
  const pollForTemplateChangesOnRefocus = usePromptTemplatesStore((store) => store.pollForTemplateChangesOnRefocus);
  const setPollForTemplateChangesOnRefocus = usePromptTemplatesStore((store) => store.setPollForTemplateChangesOnRefocus);
  const filters = usePromptTemplatesStore((store) => store.filters);
  const previousTemplatesRef = React.useRef<typeof promptTemplates>([])
  const pollCountRef = React.useRef(0)
  const pollIntervalRef = React.useRef<ReturnType<typeof setInterval>>(undefined)
  const isPollingRef = React.useRef(false)

  const checkForTemplateChanges = React.useCallback(async () => {
    if (!accountId) return

    try {
      // Determine which matterTypeId to use based on current filter
      const selectedMatterTypeId = filters.matterType[0]
      const fetchMatterTypeId = selectedMatterTypeId === 'all' ? undefined : selectedMatterTypeId

      const templatesData = await getTemplates({
        matterTypeId: fetchMatterTypeId
      })
      if (!templatesData?.data) return

      const hasChanges = JSON.stringify(templatesData.data) !== JSON.stringify(previousTemplatesRef.current)

      if (hasChanges) {
        setPromptTemplates(templatesData.data)
        setPollForTemplateChangesOnRefocus(false)
        // Clear interval if changes detected
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current)
          pollIntervalRef.current = undefined
          pollCountRef.current = 0
          isPollingRef.current = false
        }
      } else if (pollCountRef.current >= 15) {
        // Clear interval after 15 attempts if no changes
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current)
          pollIntervalRef.current = undefined
          pollCountRef.current = 0
          isPollingRef.current = false
        }
        setPollForTemplateChangesOnRefocus(false)
      }

      pollCountRef.current++
    } catch (error) {
      console.error('Error checking for template changes:', error)
    }
  }, [accountId, setPromptTemplates, setPollForTemplateChangesOnRefocus, filters.matterType])

  React.useEffect(() => {
    if (!pollForTemplateChangesOnRefocus) return

    const handleFocus = () => {
      if (isPollingRef.current) {
        return
      }

      previousTemplatesRef.current = promptTemplates
      pollCountRef.current = 0
      isPollingRef.current = true

      pollIntervalRef.current = setInterval(checkForTemplateChanges, 2000)
    }

    window.addEventListener('focus', handleFocus)
    return () => {
      window.removeEventListener('focus', handleFocus)
      // Only clear the interval if component is unmounting
      // Do not clear on blur events
      if (!pollForTemplateChangesOnRefocus) {
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current)
          isPollingRef.current = false
        }
      }
    }
  }, [pollForTemplateChangesOnRefocus, accountId, promptTemplates, checkForTemplateChanges])
}
